import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { theme } from '../../theme';
import { CATEGORIES, JOB_TYPES } from '../../constants';

// ─── SVG icons from /public/icons/jobs/ ───────────────────────
const ICON_FILES = [
  'moving-truck', 'sofa-move', 'washing-machine', 'electronics',
  'desk-chair', 'ikea-tools', 'furniture-recycle', 'garden-rake',
  'lawn-mower', 'packing-boxes', 'grass-cutting', 'curtain-rod',
  'bbq-grill', 'computer-setup', 'fridge-disposal', 'can-recycling',
  'old-fridge', 'ferry-ride',
];

// ─── Íslenskir brandara ──────────────────────────────────────
const JOKES = [
  "Af hverju fór sófinn í bíl? Hann var orðinn þreyttur á að liggja!",
  "Hvað sagði ruslafatan við sorpbílinn? Þú lyftir mér alltaf upp!",
  "Af hverju var garðyrkjumaðurinn svona glaður? Hann fann rót vandans!",
  "Hvað sagði hamarinn við naglann? Ég er alveg sleginn á þig!",
  "Af hverju fór ísskápurinn á Sorpu? Hann var búinn að kólna.",
  "Hvað sagði hurðin við vegginn? Þú heldur alltaf á mér!",
  "Af hverju gat flutningsmennirnir ekki hætt? Þeir voru alltaf á iðinu.",
  "Hvað sagði pallbíllinn við fólksbílinn? Ég ber meira úr býtum!",
  "Af hverju fór málverkið í flutninga? Það var fastur í ramma.",
  "Hvað sagði þvottavélin við þurrkarana? Þú snúrð mér alltaf á svona!",
  "Af hverju var bókahillan svona þreytt? Hún stóð undir miklu álagi.",
  "Hvað sagði ljósin við innstunguna? Án þín er ég ekki neitt!",
  "Af hverju fór tölvan á Sorpu? Hún var búin að hanga of oft.",
  "Hvað sagði teppilagnarmaðurinn? Ég legg alltaf gott að borðinu!",
  "Af hverju eru flutningamenn alltaf í forsvari? Þeir bera sig vel.",
  "Hvað sagði grasið við sláttuvélina? Kipptu þér ekki upp!",
  "Af hverju tók gardínan verkefnið? Hún vildi enda upphengt.",
];

// ─── Shuffle helper ───────────────────────────────────────────
function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─── Floating background icons component ─────────────────────
function FloatingIcons({ iconCount = 8 }) {
  const [visibleIcons, setVisibleIcons] = useState(() => {
    return shuffleArray(ICON_FILES).slice(0, iconCount).map((name, i) => ({
      name,
      id: `${name}-${i}`,
      top: `${5 + Math.random() * 80}%`,
      left: `${5 + Math.random() * 85}%`,
      rotation: Math.floor(Math.random() * 60 - 30),
      opacity: 0.15 + Math.random() * 0.12,
      size: 80 + Math.floor(Math.random() * 80),
      dur: 5 + Math.random() * 5,
      delay: Math.random() * 4,
    }));
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleIcons(prev => {
        const available = ICON_FILES.filter(
          n => !prev.some(p => p.name === n)
        );
        const shuffled = shuffleArray(available);
        return prev.map((icon, i) => {
          if (Math.random() < 0.5 && shuffled.length > 0) {
            const newName = shuffled.pop();
            return {
              ...icon,
              name: newName,
              top: `${5 + Math.random() * 80}%`,
              left: `${5 + Math.random() * 85}%`,
              rotation: Math.floor(Math.random() * 60 - 30),
              opacity: 0.15 + Math.random() * 0.12,
              size: 80 + Math.floor(Math.random() * 80),
            };
          }
          return icon;
        });
      });
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={{
      position: 'absolute', inset: 0, overflow: 'hidden',
      pointerEvents: 'none', zIndex: 0,
    }}>
      {visibleIcons.map(icon => (
        <img
          key={icon.id}
          src={`/icons/jobs/${icon.name}.svg`}
          alt=""
          className="desktop-float"
          style={{
            position: 'absolute',
            top: icon.top,
            left: icon.left,
            width: icon.size,
            height: icon.size,
            opacity: icon.opacity,
            filter: 'brightness(1.5) saturate(0.5)',
            transition: 'top 2.5s ease, left 2.5s ease, opacity 2s ease',
            '--rot': `${icon.rotation}deg`,
            '--dur': `${icon.dur}s`,
            '--delay': `${icon.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

// ─── Brandari dagsins ─────────────────────────────────────────
function JokeOfTheDay() {
  const jokeIndex = new Date().getDate() % JOKES.length;
  const joke = JOKES[jokeIndex];

  return (
    <div style={{
      background: `linear-gradient(135deg, ${theme.card}, ${theme.surface})`,
      border: `1px solid ${theme.border}`,
      borderRadius: 16,
      padding: '24px 28px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: -10, right: -10,
        fontSize: 80, opacity: 0.06, transform: 'rotate(12deg)',
        lineHeight: 1, pointerEvents: 'none',
      }}>
        😄
      </div>
      <div style={{
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.12em', color: theme.orange,
        marginBottom: 10,
      }}>
        Brandari dagsins
      </div>
      <div style={{
        fontSize: 16, lineHeight: 1.6,
        color: theme.text, fontStyle: 'italic',
      }}>
        {joke}
      </div>
    </div>
  );
}

// ─── API base for landing background (same logic as api.js) ──
const API_BASE = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8787'
  : (import.meta.env.VITE_API_URL || 'https://api.greidi.is');

// ─── Main DesktopLanding component ────────────────────────────
export default function DesktopLanding({ children }) {
  const [isDesktop, setIsDesktop] = useState(false);
  const [bgImageUrl, setBgImageUrl] = useState(null);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    setIsDesktop(mq.matches);
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Load display font for desktop
  useEffect(() => {
    if (!isDesktop) return;
    if (document.querySelector('link[data-desktop-font]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.dataset.desktopFont = '1';
    link.href = 'https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&display=swap';
    document.head.appendChild(link);
  }, [isDesktop]);

  // Fetch AI-generated landing background
  useEffect(() => {
    if (!isDesktop) return;
    const fetchBg = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/ai/landing-bg`);
        if (res.ok && res.status === 200) {
          const blob = await res.blob();
          setBgImageUrl(URL.createObjectURL(blob));
        }
      } catch {
        // Fallback to gradient (no bg image)
      }
    };
    fetchBg();
    return () => {
      if (bgImageUrl) URL.revokeObjectURL(bgImageUrl);
    };
  }, [isDesktop]);

  // Mobile: just render children, zero overhead
  if (!isDesktop) {
    return children;
  }

  const heroSubtext = "Berðu fram óskir eftir greiða — eða gerðu öðrum greiða.";

  const howItWorks = [
    { step: "01", icon: "📸", title: "Taktu mynd", desc: "Taktu mynd af verkefninu eða lýstu því. Gervigreindin okkar sér um flokkun og verðmat." },
    { step: "02", icon: "🤝", title: "Fáðu tilboð", desc: "Verktakar sjá verkið og bjóða í það. Þú velur besta tilboðið." },
    { step: "03", icon: "✅", title: "Láttu gera við", desc: "Verktakinn kemur og sinnir verkinu. Greiðsla eftir vel lokið verk." },
  ];

  // ─── Styles ─────────────────────────────────────────────────
  const s = {
    root: {
      display: 'flex',
      minHeight: '100vh',
      background: `linear-gradient(135deg, #0f1629 0%, #162035 40%, #1a2540 70%, #0f1629 100%)`,
      fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
      color: theme.text,
      position: 'relative',
      overflow: 'hidden',
    },
    left: {
      flex: '0 0 55%',
      maxWidth: '55%',
      overflowY: 'auto',
      padding: '60px 48px 80px 56px',
      position: 'relative',
      zIndex: 1,
    },
    right: {
      flex: '0 0 45%',
      maxWidth: '45%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'sticky',
      top: 0,
      height: '100vh',
      zIndex: 2,
    },
    phone: {
      width: 390,
      height: '85vh',
      maxHeight: 844,
      minHeight: 600,
      borderRadius: 44,
      overflow: 'hidden',
      position: 'relative',
      transform: 'translateZ(0)',  // Contains position:fixed children
      background: theme.bg,
      boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 2px rgba(255,255,255,0.05)',
      animation: 'phonePulse 4s ease-in-out infinite',
    },
    phoneContent: {
      width: '100%',
      height: '100%',
      overflow: 'auto',
      position: 'relative',
    },
    bezel: {
      position: 'absolute',
      top: 0, left: 0, right: 0,
      height: 44,
      background: `linear-gradient(to bottom, ${theme.bg}, transparent)`,
      zIndex: 20,
      pointerEvents: 'none',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingTop: 8,
    },
    dynamicIsland: {
      width: 120,
      height: 32,
      borderRadius: 20,
      background: '#000',
      boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
    },
    homeIndicator: {
      position: 'absolute',
      bottom: 6,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 134,
      height: 5,
      borderRadius: 3,
      background: 'rgba(255,255,255,0.15)',
      zIndex: 20,
      pointerEvents: 'none',
    },
    // Typography
    badge: {
      display: 'inline-block',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: theme.accent,
      marginBottom: 16,
      opacity: 0.9,
    },
    h1: {
      fontFamily: "'Syne', 'DM Sans', system-ui, sans-serif",
      fontSize: 56,
      fontWeight: 800,
      lineHeight: 1.08,
      letterSpacing: '-0.03em',
      textAlign: 'center',
      marginBottom: 12,
      background: `linear-gradient(135deg, ${theme.text} 0%, ${theme.accent} 50%, ${theme.green} 100%)`,
      backgroundSize: '200% 200%',
      animation: 'gradientShift 6s ease infinite',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    h2: {
      fontFamily: "'Syne', 'DM Sans', system-ui, sans-serif",
      fontSize: 28,
      fontWeight: 700,
      letterSpacing: '-0.02em',
      color: theme.text,
      marginBottom: 24,
    },
    subtitle: {
      fontFamily: "'Syne', 'DM Sans', system-ui, sans-serif",
      fontSize: 22,
      fontWeight: 600,
      color: theme.accent,
      marginBottom: 20,
      lineHeight: 1.3,
    },
    bodyText: {
      fontSize: 16,
      lineHeight: 1.7,
      color: theme.textMuted,
      maxWidth: 520,
    },
    section: {
      marginBottom: 64,
    },
    divider: {
      height: 1,
      background: `linear-gradient(to right, transparent, ${theme.border}, transparent)`,
      margin: '48px 0',
    },
  };

  return (
    <div style={s.root}>
      {/* AI-generated background image */}
      {bgImageUrl && (
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${bgImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.3,
          pointerEvents: 'none',
          zIndex: 0,
        }} />
      )}
      <FloatingIcons iconCount={10} />

      {/* ─── Left column: Landing content ─── */}
      <div style={s.left}>

        {/* Hero */}
        <div style={{ ...s.section, marginBottom: 48 }} className="desktop-slide-left">
          <div style={s.badge}>Jafningjaþjónusta á Íslandi</div>
          <h1 style={s.h1}>
            Vantar ykkur<br />Greiða?
          </h1>
          <div style={s.subtitle}>
            Greiði.is er til þjónustu
          </div>
          <p style={s.bodyText}>
            {heroSubtext}
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 32 }}>
            <button
              onClick={() => {
                const phone = document.getElementById('desktop-phone-frame');
                if (phone) phone.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
              style={{
                padding: '14px 36px',
                fontSize: 16,
                fontWeight: 700,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                cursor: 'pointer',
                background: `linear-gradient(135deg, ${theme.accent}, ${theme.green})`,
                boxShadow: `0 4px 20px ${theme.accentGlow}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={e => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 8px 30px ${theme.accentGlow}`;
              }}
              onMouseLeave={e => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 4px 20px ${theme.accentGlow}`;
              }}
            >
              Byrja n&uacute;na
            </button>
            <a
              href="https://greidi.is"
              style={{
                padding: '14px 28px',
                fontSize: 16,
                fontWeight: 600,
                color: theme.textMuted,
                border: `1px solid ${theme.border}`,
                borderRadius: 12,
                textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                transition: 'border-color 0.2s, color 0.2s',
              }}
              onMouseEnter={e => { e.target.style.borderColor = theme.accent; e.target.style.color = theme.text; }}
              onMouseLeave={e => { e.target.style.borderColor = theme.border; e.target.style.color = theme.textMuted; }}
            >
              Skoða vefsíðu &rarr;
            </a>
          </div>
        </div>

        {/* Brandari dagsins */}
        <div style={{ marginBottom: 40 }} className="desktop-slide-left">
          <JokeOfTheDay />
        </div>

        <div style={s.divider} />

        {/* Flokkar */}
        <div style={s.section} className="desktop-slide-left">
          <h2 style={s.h2}>Hvað vantar að gera?</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 14,
          }}>
            {CATEGORIES.map((cat, i) => (
              <div
                key={cat.id}
                style={{
                  background: theme.card,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 14,
                  padding: '20px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  transition: 'transform 0.2s, border-color 0.3s',
                  cursor: 'default',
                  animation: `fadeIn 0.4s ease-out ${0.1 * i}s both`,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = cat.color;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = theme.border;
                }}
              >
                <span style={{ fontSize: 28 }}>{cat.icon}</span>
                <span style={{
                  fontSize: 14, fontWeight: 700, color: theme.text,
                }}>{cat.label}</span>
                <span style={{
                  width: 32, height: 3, borderRadius: 2,
                  background: cat.color, opacity: 0.6,
                }} />
              </div>
            ))}
          </div>
        </div>

        <div style={s.divider} />

        {/* Hvernig virkar */}
        <div style={s.section} className="desktop-slide-left">
          <h2 style={s.h2}>Hvernig virkar Greiði?</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {howItWorks.map((step, i) => (
              <div
                key={step.step}
                style={{
                  display: 'flex', gap: 20, alignItems: 'flex-start',
                  animation: `fadeIn 0.4s ease-out ${0.15 * i}s both`,
                }}
              >
                <div style={{
                  flex: '0 0 56px', height: 56,
                  borderRadius: 16,
                  background: `linear-gradient(135deg, ${theme.card}, ${theme.surface})`,
                  border: `1px solid ${theme.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 26,
                }}>
                  {step.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4,
                  }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: theme.accent,
                      letterSpacing: '0.08em', opacity: 0.6,
                    }}>
                      {step.step}
                    </span>
                    <span style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>
                      {step.title}
                    </span>
                  </div>
                  <p style={{
                    fontSize: 14, lineHeight: 1.6, color: theme.textMuted, margin: 0,
                  }}>
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={s.divider} />

        {/* Verktegundir */}
        <div style={s.section} className="desktop-slide-left">
          <h2 style={s.h2}>Verktegundir</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 12,
          }}>
            {JOB_TYPES.map((jt, i) => (
              <div
                key={jt.id}
                style={{
                  background: theme.card,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 12,
                  padding: '16px 18px',
                  display: 'flex', alignItems: 'center', gap: 12,
                  animation: `fadeIn 0.4s ease-out ${0.08 * i}s both`,
                  transition: 'transform 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <span style={{ fontSize: 24 }}>{jt.icon}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>
                    {jt.label}
                  </div>
                  <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.4, marginTop: 2 }}>
                    {jt.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={s.divider} />

        {/* Tölur / traust */}
        <div style={s.section} className="desktop-slide-left">
          <h2 style={s.h2}>Af hverju Greiði?</h2>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16,
          }}>
            {[
              { num: "100%", label: "Íslenskt", desc: "Allt viðmót á íslensku" },
              { num: "AI", label: "Gervigreind", desc: "Sjálfvirk flokkun og verðmat" },
              { num: "24/7", label: "Aðgengi", desc: "Stofnaðu verk hvenær sem er" },
            ].map((stat, i) => (
              <div key={i} style={{
                textAlign: 'center',
                padding: '28px 16px',
                background: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: 14,
                animation: `fadeIn 0.4s ease-out ${0.1 * i}s both`,
              }}>
                <div style={{
                  fontFamily: "'Syne', system-ui, sans-serif",
                  fontSize: 32, fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.accent}, ${theme.green})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: 4,
                }}>
                  {stat.num}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: theme.text, marginBottom: 4 }}>
                  {stat.label}
                </div>
                <div style={{ fontSize: 12, color: theme.textMuted }}>
                  {stat.desc}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={s.divider} />

        {/* CTA */}
        <div style={{ ...s.section, textAlign: 'center', paddingBottom: 40 }} className="desktop-slide-left">
          <h2 style={{ ...s.h2, marginBottom: 12 }}>
            Tilbúin(n) að byrja?
          </h2>
          <p style={{ ...s.bodyText, margin: '0 auto 28px', textAlign: 'center' }}>
            Skráðu þig og upplifðu hvernig Greiði einfaldar daglegt líf.
          </p>
          <button
            onClick={() => {
              const phone = document.getElementById('desktop-phone-frame');
              if (phone) phone.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
            style={{
              padding: '16px 48px',
              fontSize: 17,
              fontWeight: 700,
              color: '#fff',
              border: 'none',
              borderRadius: 14,
              cursor: 'pointer',
              background: `linear-gradient(135deg, ${theme.accent}, ${theme.green})`,
              boxShadow: `0 4px 24px ${theme.accentGlow}`,
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => {
              e.target.style.transform = 'translateY(-2px) scale(1.02)';
              e.target.style.boxShadow = `0 8px 36px ${theme.accentGlow}`;
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = `0 4px 24px ${theme.accentGlow}`;
            }}
          >
            Byrja n&uacute;na
          </button>

          {/* Footer */}
          <div style={{
            marginTop: 56, paddingTop: 24,
            borderTop: `1px solid ${theme.border}`,
            fontSize: 12, color: theme.textDim, lineHeight: 1.8,
          }}>
            <div>&copy; 2026 &mdash; 2076 ehf. &bull; Greiði.is</div>
            <div style={{ marginTop: 4 }}>
              Smíðað af ástarhug á Íslandi
            </div>
          </div>
        </div>
      </div>

      {/* ─── Right column: Phone mockup ─── */}
      <div style={s.right} className="desktop-slide-right">
        <div id="desktop-phone-frame" style={s.phone}>
          {/* Dynamic Island */}
          <div style={s.bezel}>
            <div style={s.dynamicIsland} />
          </div>

          {/* Live app inside phone */}
          <div style={s.phoneContent}>
            {children}
          </div>

          {/* Home indicator */}
          <div style={s.homeIndicator} />
        </div>
      </div>
    </div>
  );
}
